import type { WooCommerceProduct, WooCommerceImage, WooCommerceCategory, WooCommerceTag, WooCommerceAttribute } from '@/types/woocommerce';

/**
 * Strips currency symbols and formatting from price strings
 * Converts "$39.99" -> "39.99" or "$1,234.56" -> "1234.56"
 */
function parsePriceString(price: string | number | null | undefined): string {
  if (!price) return '0';
  if (typeof price === 'number') return String(price);
  
  // Remove currency symbols, spaces, and commas
  const cleaned = String(price)
    .replace(/[$€£¥,\s]/g, '')
    .trim();
  
  // Parse to ensure it's a valid number, then return as string
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? '0' : String(parsed);
}

/**
 * Replaces demo17 URLs with the current WordPress URL from environment
 * This fixes cases where demo2 shares data with demo17 (extension)
 * Works both at build time (SSR) and runtime (client-side)
 */
function normalizeImageUrl(url: string | null | undefined): string {
  if (!url) return '';
  
  // Get WordPress URL from environment (works in both Node.js and browser)
  const currentWpUrl = typeof window !== 'undefined' 
    ? (window as any).__NEXT_DATA__?.env?.NEXT_PUBLIC_WORDPRESS_URL || 
      (window as any).__NEXT_DATA__?.env?.WORDPRESS_URL ||
      process.env.NEXT_PUBLIC_WORDPRESS_URL
    : process.env.NEXT_PUBLIC_WORDPRESS_URL || process.env.WORDPRESS_URL || '';
  
  if (!currentWpUrl) return url;
  
  // Replace demo17.beauty.local with current domain if URL contains demo17
  // This handles cases where demo2 and demo17 share the same WordPress database
  try {
    const wpBaseUrl = currentWpUrl.replace(/\/$/, '');
    const wpHostname = new URL(wpBaseUrl).hostname;
    const wpProtocol = new URL(wpBaseUrl).protocol;
    
    // Replace demo17 URLs in various formats:
    // - https://demo17.beauty.local/...
    // - http://demo17.beauty.local/...
    // - //demo17.beauty.local/... (protocol-relative)
    // - demo17.beauty.local/... (no protocol)
    return url
      .replace(/https?:\/\/demo17\.beauty\.local/g, wpBaseUrl)
      .replace(/\/\/demo17\.beauty\.local/g, `${wpProtocol}//${wpHostname}`)
      .replace(/demo17\.beauty\.local/g, wpHostname);
  } catch {
    // If URL parsing fails, just do simple string replacement
    const wpBaseUrl = currentWpUrl.replace(/\/$/, '');
    return url
      .replace(/https?:\/\/demo17\.beauty\.local/g, wpBaseUrl)
      .replace(/demo17\.beauty\.local/g, wpBaseUrl.replace(/https?:\/\//, '').split('/')[0]);
  }
}

/**
 * Transforms GraphQL product data to match WooCommerceProduct interface
 * Handles field name differences between GraphQL and REST API formats
 */
export function transformGraphQLProduct(graphQLProduct: any): WooCommerceProduct {
  // Transform images from GraphQL format to WooCommerce format
  const images: WooCommerceImage[] = [];
  
  // Add featured image (image field)
  if (graphQLProduct.image?.sourceUrl) {
    const normalizedUrl = normalizeImageUrl(graphQLProduct.image.sourceUrl);
    images.push({
      id: parseInt(graphQLProduct.image.id) || 0,
      src: normalizedUrl,
      name: graphQLProduct.image.altText || graphQLProduct.name,
      alt: graphQLProduct.image.altText || graphQLProduct.name,
      position: 0,
      date_created: graphQLProduct.date || '',
      date_modified: graphQLProduct.modified || '',
    });
  }
  
  // Add gallery images
  if (graphQLProduct.galleryImages?.nodes) {
    graphQLProduct.galleryImages.nodes.forEach((img: any, index: number) => {
      if (img.sourceUrl) {
        const normalizedUrl = normalizeImageUrl(img.sourceUrl);
        images.push({
          id: parseInt(img.id) || 0,
          src: normalizedUrl,
          name: img.altText || `${graphQLProduct.name} ${index + 1}`,
          alt: img.altText || `${graphQLProduct.name} ${index + 1}`,
          position: index + 1,
          date_created: graphQLProduct.date || '',
          date_modified: graphQLProduct.modified || '',
        });
      }
    });
  }
  
  // Transform categories
  const categories: WooCommerceCategory[] = (graphQLProduct.productCategories?.nodes || []).map((cat: any) => ({
    id: parseInt(cat.id) || 0,
    name: cat.name,
    slug: cat.slug,
    parent: 0, // GraphQL doesn't provide parent in this query
    description: cat.description || '',
    display: 'default' as const,
    image: cat.image?.sourceUrl ? {
      id: 0,
      src: cat.image.sourceUrl,
      name: cat.image.altText || cat.name,
      alt: cat.image.altText || cat.name,
      position: 0,
      date_created: '',
      date_modified: '',
    } : null,
    menu_order: 0,
    count: 0,
  }));
  
  // Transform tags
  const tags: WooCommerceTag[] = (graphQLProduct.productTags?.nodes || []).map((tag: any) => ({
    id: parseInt(tag.id) || 0,
    name: tag.name,
    slug: tag.slug,
    description: '',
    count: 0,
  }));
  
  // Transform attributes
  const attributes: WooCommerceAttribute[] = (graphQLProduct.attributes?.nodes || []).map((attr: any) => ({
    id: parseInt(attr.id) || 0,
    name: attr.name,
    slug: attr.slug || attr.name.toLowerCase().replace(/\s+/g, '-'),
    position: 0,
    visible: attr.visible || false,
    variation: attr.variation || false,
    options: attr.options || [],
  }));
  
  // Build the transformed product
  const transformed: WooCommerceProduct = {
    // Prefer databaseId; fall back to productId (wp-graphql-woocommerce's
    // integer post ID alias); final fallback tries parsing `id` which usually
    // fails since it's a base64 global ID.
    id: graphQLProduct.databaseId || graphQLProduct.productId || parseInt(graphQLProduct.id) || 0,
    name: graphQLProduct.name || '',
    slug: graphQLProduct.slug || '',
    permalink: `/${graphQLProduct.slug || ''}`,
    date_created: graphQLProduct.date || '',
    date_modified: graphQLProduct.modified || '',
    type: (graphQLProduct.type?.toLowerCase() || 'simple') as 'simple' | 'grouped' | 'external' | 'variable',
    status: graphQLProduct.status || 'publish',
    featured: graphQLProduct.featured || false,
    catalog_visibility: (graphQLProduct.catalogVisibility?.toLowerCase() || 'visible') as 'visible' | 'catalog' | 'search' | 'hidden',
    description: graphQLProduct.description || '',
    short_description: graphQLProduct.shortDescription || '',
    sku: graphQLProduct.sku || '',
    price: parsePriceString(graphQLProduct.price),
    regular_price: parsePriceString(graphQLProduct.regularPrice || graphQLProduct.price),
    sale_price: parsePriceString(graphQLProduct.salePrice),
    date_on_sale_from: null,
    date_on_sale_to: null,
    on_sale: graphQLProduct.onSale || false,
    purchasable: true,
    total_sales: graphQLProduct.totalSales || 0,
    virtual: false,
    downloadable: false,
    downloads: [],
    download_limit: -1,
    download_expiry: -1,
    external_url: '',
    button_text: '',
    tax_status: 'taxable',
    tax_class: '',
    manage_stock: graphQLProduct.manageStock ?? false,
    stock_quantity: graphQLProduct.stockQuantity ?? null,
    // Transform GraphQL stockStatus enum (IN_STOCK, OUT_OF_STOCK, ON_BACKORDER) to lowercase format
    stock_status: (() => {
      if (!graphQLProduct.stockStatus) return 'instock';
      // Handle GraphQL enum format: IN_STOCK, OUT_OF_STOCK, ON_BACKORDER
      const status = String(graphQLProduct.stockStatus).toUpperCase();
      if (status === 'IN_STOCK' || status === 'INSTOCK') return 'instock';
      if (status === 'OUT_OF_STOCK' || status === 'OUTOFSTOCK') return 'outofstock';
      if (status === 'ON_BACKORDER' || status === 'ONBACKORDER') return 'onbackorder';
      // Try lowercase version
      const lowerStatus = status.toLowerCase().replace(/_/g, '');
      if (lowerStatus === 'instock') return 'instock';
      if (lowerStatus === 'outofstock') return 'outofstock';
      if (lowerStatus === 'onbackorder') return 'onbackorder';
      return 'instock'; // Default fallback
    })() as 'instock' | 'outofstock' | 'onbackorder',
    backorders: 'no',
    backorders_allowed: false,
    backordered: false,
    low_stock_amount: null,
    sold_individually: false,
    weight: graphQLProduct.weight || '',
    dimensions: {
      length: graphQLProduct.length || '',
      width: graphQLProduct.width || '',
      height: graphQLProduct.height || '',
    },
    shipping_required: true,
    shipping_taxable: true,
    shipping_class: '',
    shipping_class_id: 0,
    reviews_allowed: true,
    average_rating: String(graphQLProduct.averageRating || '0'),
    rating_count: graphQLProduct.reviewCount || 0,
    upsell_ids: [],
    cross_sell_ids: [],
    parent_id: 0,
    purchase_note: '',
    categories,
    tags,
    images,
    attributes,
    default_attributes: [],
    variations: [],
    grouped_products: [],
    menu_order: graphQLProduct.menuOrder || 0,
    price_html: '',
    related_ids: [],
    meta_data: [],
  };
  
  return transformed;
}

/**
 * Transforms an array of GraphQL products
 */
export function transformGraphQLProducts(graphQLProducts: any[]): WooCommerceProduct[] {
  if (!graphQLProducts || !Array.isArray(graphQLProducts)) {
    return [];
  }
  
  return graphQLProducts.map(transformGraphQLProduct);
}

// TransformBlocks utility is now available at '@/utils/transformBlocks'
// Import and use: import { TransformBlocks } from '@/utils/transformBlocks';

